import { useSnackbar } from "notistack";
import React, { useState, useEffect } from "react";
import {
  DataLoaderOption,
  DataOption,
  getDataLoaderSystem,
  getDataLoaderOptions,
  IntegrationOptions,
  getIntegratedDataLoader,
  updateDataLoaderAdaptorProperties,
  getDataSystemEventTypes,
} from "../../api/datasystems";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InfoIcon from "@mui/icons-material/Info";

export interface DataLoaderSystemComponentContent {
  name: string;
  type: string;
  options: Record<string, string | number | boolean>;
  integrations: IntegrationOptions;
  list_ids?: string[];
  optionsLoadedFromBackend?: boolean;
}

export interface DataLoaderSystemComponentProps {
  hideHeading?: boolean;
  name: string;
  type: string;
  configRevisionId?: number;
  id?: number;
  existing?: boolean;
  disableTypeEdit?: boolean;
  disableApply?: boolean;
  hideTypeField?: boolean;
  hideNameField?: boolean;
  onDataLoaderSystemChange: (
    dataLoaderSystem: DataLoaderSystemComponentContent
  ) => void;
  dataLoaderOptions: DataLoaderOption[];
}

export function DataLoaderSystemComponent(
  props: Readonly<DataLoaderSystemComponentProps>
) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    name,
    type,
    onDataLoaderSystemChange,
    id,
    configRevisionId,
    existing,
    disableTypeEdit,
    disableApply,
    hideTypeField,
    hideNameField,
  } = props;
  const [userDefinedName, setUserDefinedName] = useState<string>(name || "");
  const [userDefinedType, setUserDefinedType] = useState<string>(type || "");

  const [userDefinedOptions, setUserDefinedOptions] = useState<
    Record<string, string | number | boolean>
  >({});
  const [integratedSystemOptions, setIntegratedSystemOptions] =
    useState<IntegrationOptions>();
  const [optionsLoadedFromBackend, setOptionsLoadedFromBackend] =
    useState<boolean>(id === undefined || configRevisionId === undefined);

  useEffect(() => {
    if (id === undefined) {
      setUserDefinedName(name || "");
    }
  }, [id, name]);

  useEffect(() => {
    if (id === undefined) {
      setUserDefinedType(type || "");
    }
  }, [id, type]);

  useEffect(() => {
    let isMounted = true;

    if (id !== undefined && configRevisionId !== undefined) {
      setOptionsLoadedFromBackend(false);
      getDataLoaderSystem(id)
        .then((systemResponse) => {
          if (isMounted) {
            getDataLoaderOptions(id, configRevisionId)
              .then((dataOptionResponse) => {
                let selectedOptions: Record<string, string | number | boolean> =
                  {};
                dataOptionResponse.forEach((option) => {
                  selectedOptions[option.path] = option.value;
                });
                setUserDefinedOptions(selectedOptions);
                setOptionsLoadedFromBackend(true);
              })
              .catch((err) => {
                console.log(err);
                setOptionsLoadedFromBackend(true);
              });
            getIntegratedDataLoader(id, configRevisionId)
              .then((integratedDataLoader) => {
                setIntegratedSystemOptions(integratedDataLoader);
              })
              .catch((err) => console.log(err));
            setUserDefinedName(systemResponse.name);
            setUserDefinedType(systemResponse.system_type);
          }
        })
        .catch((err) => console.log(err));
    }
    return () => {
      isMounted = false;
    };
  }, [id, configRevisionId]);

  useEffect(() => {
    onDataLoaderSystemChange({
      name: userDefinedName,
      type: userDefinedType,
      options: userDefinedOptions,
      integrations: integratedSystemOptions,
      optionsLoadedFromBackend,
    });
  }, [
    userDefinedName,
    userDefinedType,
    userDefinedOptions,
    integratedSystemOptions,
    optionsLoadedFromBackend,
  ]);

  const handleSystemTypeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setUserDefinedType(event.target.value as string);
    setUserDefinedOptions({});
    setOptionsLoadedFromBackend(true);
    setIntegratedSystemOptions({
      ...integratedSystemOptions,
      EmailMarketing: "",
      TimeAndBilling: "",
    });
  };

  const updateUserDefinedOptions = (
    value: string | number | boolean,
    option: DataOption
  ) => {
    if (!option.path) {
      return;
    }
    setUserDefinedOptions({ ...userDefinedOptions, [option.path]: value });
  };

  return (
    <FormControl
      sx={{
        width: "100%",
        margin: 0,
      }}
    >
      {existing && id != null && !props.hideHeading && (
        <Typography
          component="h3"
          variant="h6"
          id={`system-${id}`}
          tabIndex={-1}
          sx={{ scrollMarginTop: "100px", overflowWrap: "anywhere" }}
        >
          {userDefinedName || name}
        </Typography>
      )}
      {!hideNameField ? (
        <TextField
          name={"data_loader_system_name"}
          required
          fullWidth
          label="Name"
          value={userDefinedName}
          variant="outlined"
          size="small"
          margin="dense"
          disabled={existing === true}
          onChange={(event) => {
            setUserDefinedName(event.target.value);
          }}
        />
      ) : null}
      <Container style={{ padding: 0 }}>
        <Typography>Modes</Typography>
      </Container>

      {props.dataLoaderOptions && !hideTypeField ? (
        <TextField
          select
          required
          fullWidth
          label="Type"
          variant="outlined"
          onChange={handleSystemTypeChange}
          value={userDefinedType}
          size="small"
          margin="dense"
          disabled={disableTypeEdit}
        >
          {props.dataLoaderOptions.map((dataLoaderOption) => {
            return (
              <MenuItem
                key={dataLoaderOption.type.toLowerCase()}
                value={dataLoaderOption.type}
              >
                {dataLoaderOption.type}
              </MenuItem>
            );
          })}
        </TextField>
      ) : null}

      {userDefinedType && props.dataLoaderOptions
        ? props.dataLoaderOptions
            .filter((item) => {
              return item.type === userDefinedType;
            })
            .flatMap((dataLoaderOption) => {
              return dataLoaderOption.options.map((option) => {
                const optionValue = option.path
                  ? userDefinedOptions[option.path]
                  : undefined;
                return (
                  <Tooltip title={option.description} key={option.path}>
                    <div>
                      {option.type === "bool" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(optionValue)}
                              onChange={(_event, checked) => {
                                updateUserDefinedOptions(checked, option);
                              }}
                            />
                          }
                          label={option.name}
                        />
                      ) : (
                        <TextField
                          required
                          fullWidth
                          variant="outlined"
                          size="small"
                          margin="dense"
                          type={
                            option.type === "password" ||
                            option.name === "App client secret"
                              ? "password"
                              : null
                          }
                          multiline={option.type === "json"}
                          minRows={5}
                          key={option.path}
                          label={option.name}
                          value={optionValue ?? ""}
                          onChange={(event) => {
                            updateUserDefinedOptions(
                              event.target.value,
                              option
                            );
                          }}
                        />
                      )}
                      {option.name.includes("Marketing Activity IDs") && (
                        <IconButton
                          size={"small"}
                          color={"primary"}
                          onClick={() => {
                            getDataSystemEventTypes(id, configRevisionId).then(
                              (response) => console.log(response)
                            );
                          }}
                        >
                          <InfoIcon fontSize={"small"} />
                        </IconButton>
                      )}
                    </div>
                  </Tooltip>
                );
              });
            })
        : null}
      {!disableApply && id !== undefined && configRevisionId !== undefined ? (
        <Button
          onClick={() => {
            let properties = [];
            for (let key in userDefinedOptions) {
              properties.push({
                name: key,
                value: userDefinedOptions[key],
              });
            }
            updateDataLoaderAdaptorProperties(
              id,
              configRevisionId,
              properties,
              userDefinedType
            )
              .then(() => {
                enqueueSnackbar("Source settings saved successfully!", {
                  variant: "success",
                });
              })
              .catch(() => {
                enqueueSnackbar("Unable to save source settings.", {
                  variant: "error",
                });
              });
          }}
        >
          Apply
        </Button>
      ) : null}
    </FormControl>
  );
}
